import { Check, ShieldCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { miles } from "@/lib/inventory";
import { Reveal } from "@/components/motion/Reveal";

const H2 = ({ children }) => <h2 className="font-display text-2xl font-extrabold tracking-tight">{children}</h2>;

const sentenceCase = (s) => s.toLowerCase().replace(/(^|[.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase());

const specRows = (v) =>
  [
    ["Mileage", miles(v.mileage)],
    ["Registration", v.registration],
    ["Fuel type", v.fuel],
    ["Gearbox", v.transmission],
    ["Body type", v.bodyType],
    ["Engine", v.engine],
    ["Colour", v.colour],
    ["Doors", v.doors],
    ["Seats", v.seats],
    ["Owners", v.owners],
    ["Service history", v.serviceHistory],
    ["Emission class", v.emissionClass],
    ["Drivetrain", v.drivetrain],
  ].filter(([, val]) => val);

export const VehicleSections = ({ v }) => (
  <div className="mt-12 grid gap-14" data-testid="vehicle-sections">
    {v.highlights.length > 0 && (
      <Reveal>
        <H2>Highlights from the advert</H2>
        <ul className="mt-5 flex flex-wrap gap-2" data-testid="vehicle-highlights">
          {v.highlights.map((h) => (
            <li key={h} className="rounded-sm border border-line bg-paper px-3 py-2 text-sm font-semibold uppercase tracking-wide text-ink">
              {h}
            </li>
          ))}
        </ul>
      </Reveal>
    )}

    <Reveal>
      <H2>Specification summary</H2>
      <div className="mt-5 overflow-hidden rounded-lg border border-line">
        <dl className="-mb-px -mr-px grid grid-cols-2 sm:grid-cols-3" data-testid="vehicle-spec-summary">
          {specRows(v).map(([k, val]) => (
            <div key={k} className="border-b border-r border-line bg-white p-4">
              <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-steel">{k}</dt>
              <dd className="mt-1.5 font-semibold text-ink">{val}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Reveal>

    <Reveal>
      <H2>Description</H2>
      <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-steel" data-testid="vehicle-description">
        {v.description || "No description supplied for this vehicle."}
      </p>
      {v.strapline && (
        <p className="mt-6 border-l-[3px] border-brand bg-paper px-5 py-4 text-sm font-semibold leading-relaxed text-ink" data-testid="vehicle-dealer-note">
          From the dealer: {sentenceCase(v.strapline)}
        </p>
      )}
    </Reveal>

    {v.features.length > 0 && (
      <Reveal>
        <H2>Features</H2>
        <Accordion type="multiple" defaultValue={[v.features[0].title]} className="mt-4" data-testid="vehicle-features">
          {v.features.map((f) => (
            <AccordionItem key={f.title} value={f.title} className="border-line">
              <AccordionTrigger className="py-5 text-base font-semibold hover:no-underline" data-testid={`feature-group-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {f.title} <span className="ml-auto mr-3 text-sm font-normal text-steel">{f.items.length}</span>
              </AccordionTrigger>
              <AccordionContent>
                <ul className="grid gap-x-8 gap-y-2.5 pb-2 sm:grid-cols-2">
                  {f.items.map((item) => (
                    <li key={item} className="flex gap-2.5 text-[15px] text-ink">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" /> {item}
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    )}

    {(v.specs.length > 0 || v.runningCosts.length > 0) && (
      <Reveal>
        <H2>Technical data</H2>
        <div className="mt-5 grid gap-6 md:grid-cols-2">
          {[...v.specs, ...(v.runningCosts.length ? [{ category: "Running costs", items: v.runningCosts.map((r) => ({ name: r.label, value: r.value })) }] : [])].map((s) => (
            <div key={s.category} className="rounded-lg border border-line">
              <h3 className="border-b border-line px-5 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-ink">{s.category}</h3>
              <dl className="divide-y divide-line">
                {s.items.map((it) => (
                  <div key={it.name} className="flex justify-between gap-4 px-5 py-3 text-sm">
                    <dt className="text-steel">{it.name}</dt>
                    <dd className="text-right font-semibold text-ink">{it.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </Reveal>
    )}

    {v.historyCheck && (
      <Reveal>
        <div className="flex gap-4 rounded-lg border border-line bg-paper p-5" data-testid="vehicle-history-check">
          <ShieldCheck className="h-6 w-6 shrink-0 text-brand" aria-hidden="true" />
          <p className="text-sm leading-relaxed text-ink">
            <span className="font-semibold">AutoTrader history check:</span> {v.historyCheck}.{" "}
            <a href={v.autotraderUrl} target="_blank" rel="noopener noreferrer" className="link-underline font-semibold text-brand" data-testid="vehicle-history-autotrader-link">
              See the full report on AutoTrader
            </a>
          </p>
        </div>
      </Reveal>
    )}
  </div>
);
